import { DeviceId } from "hassibot/services_v2/common/types";
import { uuidv4 } from "hassibot/util/uuid";

const LOCAL_STORAGE_USER_PREFIX = "USER_STATE";
export const LOCAL_STORAGE_DEVICE_ID_KEY = `${LOCAL_STORAGE_USER_PREFIX}.DEVICE_ID`;
const LOCAL_STORAGE_PROBE_KEY = "__HB_LOCAL_STORAGE_PROBE__";

export const isLocalStorageAvailable = (): boolean => {
  try {
    localStorage.setItem(LOCAL_STORAGE_PROBE_KEY, LOCAL_STORAGE_PROBE_KEY);
    localStorage.removeItem(LOCAL_STORAGE_PROBE_KEY);
    return true;
  } catch {
    return false;
  }
};

// DEVICE_ID initializes Amplitude before React can render the local storage availability gate.
const ensureDeviceId = (): DeviceId => {
  try {
    const deviceId = localStorage?.getItem(LOCAL_STORAGE_DEVICE_ID_KEY);
    if (deviceId !== null && deviceId !== undefined) {
      return deviceId as DeviceId;
    }
  } catch {
    return uuidv4(true);
  }

  const newDeviceId = uuidv4(true);
  try {
    localStorage?.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, newDeviceId);
  } catch {
    // Keep the generated ID in memory when local storage cannot persist it.
  }
  return newDeviceId;
};

class AppConfig {
  API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  ASYNC_API_BASE_URL = import.meta.env.VITE_ASYNC_API_BASE_URL;
  API_WS_BASE_URL = import.meta.env.VITE_API_WS_BASE_URL;
  WWW_BASE_URL = import.meta.env.VITE_WWW_BASE_URL;
  CUSTOMER_SERVICE_BASE_URL = import.meta.env.VITE_CUSTOMER_SERVICE_BASE_URL;
  XIMI_BASE_URL = import.meta.env.VITE_XIMI_BASE_URL;
  IS_DEV = import.meta.env.MODE === "development";
  IS_TEST_ENVS = !!import.meta.env.VITE_IS_TEST_ENVS;
  IS_PRODUCTION_OR_TEST_ENV =
    import.meta.env.MODE === "production" || import.meta.env.MODE === "staging";
  BUILD_HASH = import.meta.env.VITE_BUILD_HASH || `dev-${Math.random().toString(36).substring(3)}`;
  BUILD_TIME: string | null = import.meta.env.VITE_BUILD_TIME || null;
  SCREEB_APP_ID: string | null = null;
  STREAM_CHAT_API_KEY: string = import.meta.env.VITE_STREAM_CHAT_API_KEY;
  IS_XIMI_MIGRATION_WARNING = import.meta.env.VITE_XIMI_MIGRATION_WARNING === "true";
  IS_XIMI_MIGRATION_ONGOING = import.meta.env.VITE_XIMI_MIGRATION_ONGOING === "true";
  IS_MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === "true";
  DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  // Datadog RUM & Session Replay are disabled by default. Set
  // VITE_DATADOG_RUM_ENABLED="true" to re-enable (no code change needed).
  IS_DATADOG_RUM_ENABLED = import.meta.env.VITE_DATADOG_RUM_ENABLED === "true";
  DEBUG_SERVICE_WORKER = import.meta.env.VITE_DEBUG_SERVICE_WORKER === "true";

  private _deviceId: DeviceId | null = null;
  // lazy load device id as it accesses local storage
  // which might not be available during app initialization
  get DEVICE_ID(): DeviceId {
    if (this._deviceId === null) {
      this._deviceId = ensureDeviceId();
    }
    return this._deviceId;
  }
}

const appConfig = new AppConfig();

export default appConfig;
